import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ShieldCheck, Zap, Leaf, Snowflake, CheckCircle2, PlayCircle, Star } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
import { SEO } from '../components/SEO';
import { sortProducts } from '../utils/sortProducts';
// Reusable framer-motion fade-in preset for homepage sections.
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.5
  }
};
// Category cards for the "Shop Yarbo by Outdoor Job" section.
const categoryCards = [{
  title: 'Robot Lawn Mowers',
  desc: 'Wire-free autonomous mowing for lawns of every size, including large and demanding properties.',
  cta: 'Shop Lawn Mowers',
  tag: 'Lawn Care',
  link: '/category/lawn-mowers',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Lawn-Mower.png?v=1718000000',
  alt: 'Yarbo robot lawn mower',
  highlight: true
}, {
  title: 'Robotic Snow Blowers',
  desc: 'True 2-stage robotic snow clearing for driveways and walkways during winter weather.',
  cta: 'Shop Snow Blowers',
  tag: 'Snow Removal',
  link: '/category/snow-blowers',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Snow-Blower.png?v=1718000000',
  alt: 'Yarbo robotic snow blower'
}, {
  title: 'Robotic Leaf Blowers',
  desc: 'High-force robotic leaf and debris clearing for wooded lots and seasonal cleanup.',
  cta: 'Shop Leaf Blowers',
  tag: 'Leaf Cleanup',
  link: '/category/leaf-blowers',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Blower.png?v=1718000000',
  alt: 'Yarbo robotic leaf blower'
}, {
  title: 'Yarbo Bundles',
  desc: 'Multi-module bundles for year-round automation — mow, clear snow, and blow leaves with one platform.',
  cta: 'Shop Bundles',
  tag: 'All-Season',
  link: '/category/bundles',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Bundle.png?v=1718000000',
  alt: 'Yarbo bundle'
}, {
  title: 'Modules & Accessories',
  desc: 'Expansion modules, blades, batteries, antennas, covers, and replacement parts for existing Yarbo owners.',
  cta: 'Shop Modules & Accessories',
  tag: 'Expand',
  link: '/category/modules-accessories',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Modules.png?v=1718000000',
  alt: 'Yarbo modules and accessories'
}, {
  title: 'Complete Systems',
  desc: 'Yarbo Core plus the module needed for the job — the best starting point for new buyers.',
  cta: 'Shop Complete Systems',
  tag: 'Start Here',
  link: '/category/complete-systems',
  image: 'https://www.yarbo.com/cdn/shop/files/Y-Series-Complete-System.png?v=1718000000',
  alt: 'Yarbo complete system'
}];
// Per-bestseller bullet copy keyed by slug; fallback uses product.features.
const bestsellerBullets: Record<string, string[]> = {
  'yarbo-lawn-mower-pro': ['Heavy-duty mowing for demanding lawns', 'Slope handling up to 35°', 'Auto-recharge and resume'],
  'yarbo-lawn-mower-pro-snow-blower-blower': ['Year-round automation — mow, clear snow, blow leaves', 'Includes Yarbo Core + 3 modules', 'Best for mixed properties'],
  'yarbo-lawn-mower-snow-blower-blower': ['All-season bundle for mowing, snow, and leaves', 'Includes Yarbo Core + 3 modules', 'Best value for new buyers'],
  'yarbo-snow-blower': ['True 2-stage robotic snow clearing', 'Driveways, walkways, and large properties', 'Auto-dock and recharge'],
  'yarbo-blower': ['High-force robotic leaf and debris clearing', 'Wooded lots and seasonal cleanup', 'Compatible with the Yarbo Core'],
  'yarbo-lawn-mower': ['Wire-free autonomous mowing', 'Up to 6 acres twice per week', 'Virtual boundaries — no perimeter wire']
};
export function Home() {
  const {
    products
  } = useStore();
  const {
    addToCart
  } = useCart();
  // Sorted product lists for the homepage sections — premium systems first.
  const bestsellers = sortProducts(products.filter(p => p.primaryGroup === 'Complete System' || p.primaryGroup === 'Bundle' || p.publicCategories?.includes('Complete Systems') || p.publicCategories?.includes('Bundles')), 'price-desc').slice(0, 6);
  const bundles = sortProducts(products.filter(p => p.primaryGroup === 'Bundle' || p.publicCategories?.includes('Bundles')), 'price-desc').slice(0, 4).map(product => ({
    product,
    bestFor: product.shortDescription
  }));
  const accessories = sortProducts(products.filter(p => p.primaryGroup === 'Accessory' || p.category === 'Accessories' || p.publicCategories?.includes('Modules & Accessories')), 'price-desc').slice(0, 6);
  // Find featured products
  const bundleOrder = products.find(p => p.slug === 'yarbo-lawn-mower-pro-snow-blower-blower');
  return <div className="flex flex-col min-h-screen" data-id="element-1510">
      <SEO title="Yarbo Robotic Yard Care" description="Merem Robotics is your authorized dealer for Yarbo robotic yard care systems. Shop robotic lawn mowers, snow blowers, and leaf blowers." keywords="yarbo, robotic lawn mower, robotic snow blower, yard care robot, merem robotics" data-id="element-1511" />
      {/* Hero Section */}
      <section className="relative bg-black text-white overflow-hidden" data-id="element-1512">
        <div className="absolute inset-0" data-id="element-1513">
          <img src="https://www.yarbo.com/cdn/shop/files/Frame_2036458698.jpg" alt="Yarbo robot lawn mower with current savings up to $1,000 on select Yarbo systems" className="w-full h-full object-cover object-center" data-id="element-1514" />
          {/* Soft gradient on left side only - keeps right-side savings overlay clear */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-transparent lg:from-white/90 lg:via-white/40 lg:to-transparent" data-id="element-1515" />
        </div>

        {/* Foreground content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1516">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center min-h-[520px] lg:min-h-[620px] py-14 lg:py-20" data-id="element-1517">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-7 relative z-10" data-id="element-1518">
              <span className="inline-block text-[11px] font-extrabold tracking-[0.2em] uppercase text-yarbo-dark bg-yarbo-yellow px-3 py-1.5 rounded-full mb-5" data-id="element-1519">
                Yarbo Robotic Yard Care Systems
              </span>
              <h1 className="text-[2.25rem] sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-extrabold text-yarbo-dark leading-[1.05] mb-6 tracking-tight max-w-[520px]" data-id="element-1520">
                Take Yard Work Off Your List
              </h1>
              <p className="text-base lg:text-lg text-gray-800 mb-5 leading-relaxed max-w-xl" data-id="element-1521">
                Shop Yarbo robot lawn mowers, robotic snow blowers, leaf
                blowers, bundles, modules, and accessories from Merem Robotics.
              </p>

              <p className="inline-flex items-center text-sm font-semibold text-yarbo-dark bg-white/90 backdrop-blur-sm border border-yarbo-yellow/60 rounded-lg px-4 py-2.5 mb-7 max-w-xl shadow-sm" data-id="element-1522">
                Now is a smart time to shop Yarbo — current savings are featured
                on select systems.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-1523">
                <Link to="/shop" className="inline-flex items-center justify-center bg-yarbo-dark text-white font-bold px-7 py-4 rounded-lg hover:bg-black transition-colors shadow-lg" data-id="element-1524">
                  <ShieldCheck className="w-5 h-5 mr-2" data-id="element-1525" /> Shop Yarbo Products
                </Link>
                <Link to="/compare" className="inline-flex items-center justify-center bg-white text-yarbo-dark border-2 border-yarbo-dark font-bold px-7 py-4 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1526">
                  Compare Yarbo Systems
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="bg-gray-50 border-y border-gray-200" data-id="element-1527">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-id="element-1528">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-6" data-id="element-1529">
            {['In Stock Products', 'Nationwide Shipping', 'Complete Systems & Modules', 'Secure Online Checkout'].map(item => <li key={item} className="flex items-center justify-center sm:justify-start gap-2.5 text-sm font-semibold text-yarbo-dark" data-id="element-1530">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-yarbo-green/15" data-id="element-1531">
                  <CheckCircle2 className="w-3 h-3 text-yarbo-green" data-id="element-1532" />
                </span>
                <span data-id="element-1533">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* ============ WEEKEND BACK ============ */}
      <section className="py-12 lg:py-14 bg-white" data-id="element-1534">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1535">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-1536">
            <h2 className="text-4xl md:text-5xl font-extrabold text-yarbo-dark mb-4 leading-[1.1] tracking-tight" data-id="element-1537">
              Your Weekend Shouldn't Revolve Around Yard Work
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 leading-relaxed" data-id="element-1538">
              Yarbo helps reduce the repetitive outdoor chores that keep eating
              into your free time — mowing, snow clearing, leaf cleanup, and
              seasonal yard maintenance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-id="element-1539">
            {[{
            icon: ShieldCheck,
            title: 'Weekly Mowing',
            desc: 'Set mowing areas and schedules so the lawn gets handled without planning your weekend around cutting the grass.',
            link: '/category/lawn-mowers'
          }, {
            icon: Snowflake,
            title: 'Snow Clearing',
            desc: 'Use a dedicated Yarbo snow system to simplify driveway and path clearing when winter weather hits.',
            link: '/category/snow-blowers'
          }, {
            icon: Leaf,
            title: 'Leaves & Debris',
            desc: 'Manage leaves, clippings, and outdoor debris without dragging out the blower again and again.',
            link: '/category/leaf-blowers'
          }, {
            icon: PlayCircle,
            title: 'Multi-Season Yard Care',
            desc: 'Choose bundles and modules so one Yarbo platform can help with more than one outdoor job.',
            link: '/category/bundles'
          }].map(({
            icon: Icon,
            title,
            desc,
            link
          }) => <Link key={title} to={link} className="group bg-white border border-gray-200 hover:border-yarbo-dark hover:shadow-md transition-all rounded-2xl p-6 flex flex-col" data-id="element-1540">
                <div className="w-12 h-12 rounded-xl bg-yarbo-yellow flex items-center justify-center mb-4 group-hover:scale-105 transition-transform" data-id="element-1541">
                  <Icon className="w-6 h-6 text-yarbo-dark" data-id="element-1542" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-lg" data-id="element-1543">
                  {title}
                </h3>
                <p className="text-[15px] text-gray-800 leading-relaxed" data-id="element-1544">
                  {desc}
                </p>
              </Link>)}
          </div>

          <div className="mt-10 text-center" data-id="element-1545">
            <Link to="/shop" className="inline-flex items-center bg-yarbo-dark text-white font-bold px-7 py-3.5 rounded-lg hover:bg-black transition-colors" data-id="element-1546">
              <ShieldCheck className="w-5 h-5 mr-2" data-id="element-1547" /> Shop Yarbo Systems
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ONE MODULAR YARBO SYSTEM ============ */}
      <section className="py-14 lg:py-16 bg-gray-50 border-t border-gray-100" data-id="element-1548">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1549">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-1550">
            <span className="text-xs font-extrabold tracking-[0.15em] uppercase text-yarbo-dark/70 mb-3 block" data-id="element-1551">
              Why Yarbo
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-1552">
              One Modular Yarbo System. Multiple Outdoor Jobs.
            </h2>
            <p className="text-lg text-gray-700" data-id="element-1553">
              Yarbo is more than a robot mower. It is a modular robotic
              yard-care platform built for homeowners who want fewer repetitive
              outdoor chores, more consistent maintenance, and one system that
              can grow with their property.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-id="element-1554">
            {[{
            icon: CheckCircle2,
            title: 'Mow on Schedule',
            desc: 'Set mowing areas and schedules so the lawn is handled without dragging out the mower every week.'
          }, {
            icon: ShieldCheck,
            title: 'Recharge & Resume',
            desc: 'Yarbo is designed to return to the dock, recharge, and continue the job, helping reduce battery-life concerns on larger properties.'
          }, {
            icon: Star,
            title: 'Built for Real Terrain',
            desc: 'Tracked mobility, slope handling, and smart navigation help Yarbo work beyond small, flat lawns.'
          }, {
            icon: Zap,
            title: 'Add Modules Later',
            desc: 'Start with a complete system or bundle, then add compatible Yarbo modules and accessories as your needs change.'
          }].map(({
            icon: Icon,
            title,
            desc
          }) => <motion.div key={title} {...fadeIn} className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all rounded-2xl p-6" data-id="element-1555">
                <div className="w-11 h-11 rounded-xl bg-yarbo-dark flex items-center justify-center mb-4" data-id="element-1556">
                  <Icon className="w-5 h-5 text-yarbo-yellow" data-id="element-1557" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-lg" data-id="element-1558">
                  {title}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1559">{desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ SHOP BY OUTDOOR JOB ============ */}
      <section className="py-14 lg:py-16 bg-white" data-id="element-1560">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1561">
          <motion.div {...fadeIn} className="mb-8 lg:mb-10 max-w-3xl" data-id="element-1562">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-1563">
              Shop Yarbo by Outdoor Job
            </h2>
            <p className="text-lg text-gray-700" data-id="element-1564">
              Start with the job you want to automate, then choose a complete
              system, bundle, module, or accessory.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-id="element-1565">
            {categoryCards.map((cat, idx) => <motion.div key={cat.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} data-id="element-1566">
                <Link to={cat.link} className={`group block rounded-2xl border overflow-hidden bg-white hover:shadow-xl transition-all h-full ${cat.highlight ? 'border-yarbo-yellow ring-1 ring-yarbo-yellow' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1567">
                  <div className="aspect-[5/3] p-6 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 relative" data-id="element-1568">
                    <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider text-gray-700 bg-white px-2 py-1 rounded" data-id="element-1569">
                      {cat.tag}
                    </span>
                    <img src={cat.image} alt={cat.alt} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" data-id="element-1570" />
                  </div>
                  <div className="p-5 sm:p-6" data-id="element-1571">
                    <h3 className="text-lg font-bold text-yarbo-dark mb-2 group-hover:text-black transition-colors" data-id="element-1572">
                      {cat.title}
                    </h3>
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed" data-id="element-1573">
                      {cat.desc}
                    </p>
                    <span className="inline-flex items-center text-sm font-bold text-yarbo-dark" data-id="element-1574">
                      {cat.cta}{' '}
                      <ChevronRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1575" />
                    </span>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ BESTSELLERS ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-1576">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1577">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-1578">
            <div className="max-w-2xl" data-id="element-1579">
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-1580">
                Complete Yarbo Systems &amp; Bundles
              </h2>
              <p className="text-lg text-gray-700" data-id="element-1581">
                Shop complete Yarbo systems and multi-module bundles for mowing,
                snow clearing, leaf cleanup, and all-season robotic yard care.
              </p>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-1582">
              Shop All Products <ChevronRight className="ml-2 w-5 h-5" data-id="element-1583" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-id="element-1584">
            {bestsellers.map((p, idx) => {
            const bullets = bestsellerBullets[p.slug] || p.features.slice(0, 3);
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: idx * 0.04
            }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all flex flex-col h-full group" data-id="element-1585">
                  <Link to={`/product/${p.slug}`} className="block relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden p-5 sm:p-6" data-id="element-1586">
                    <img src={p.image} alt={`${p.name} - Yarbo robotic yard-care system`} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-1587" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 text-[11px] font-bold rounded-full px-2 py-1" data-id="element-1588">
                      <CheckCircle2 className="w-3 h-3" data-id="element-1589" /> In Stock
                    </div>
                  </Link>
                  <div className="p-5 flex flex-col flex-grow" data-id="element-1590">
                    <h3 className="text-base sm:text-lg font-bold text-yarbo-dark leading-tight mb-1 line-clamp-2" data-id="element-1591">
                      <Link to={`/product/${p.slug}`} className="hover:text-black transition-colors" data-id="element-1592">
                        {p.name}
                      </Link>
                    </h3>
                    <div className="text-xl sm:text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-1593">
                      ${p.price.toLocaleString()}
                    </div>
                    <ul className="space-y-1.5 mb-4 text-sm text-gray-700 flex-grow" data-id="element-1594">
                      {bullets.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-1595">
                          <CheckCircle2 className="w-3.5 h-3.5 text-yarbo-green mt-0.5 flex-shrink-0" data-id="element-1596" />
                          <span data-id="element-1597">{b}</span>
                        </li>)}
                    </ul>
                    <div className="mt-auto flex flex-col gap-2" data-id="element-1598">
                      <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-1599">
                        <ShieldCheck className="w-4 h-4" data-id="element-1600" /> Add to Cart
                      </button>
                      <Link to={`/product/${p.slug}`} className="w-full text-center text-sm font-semibold text-gray-700 hover:text-yarbo-dark py-1" data-id="element-1601">
                        View Details →
                      </Link>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ============ MAKES SENSE FOR REAL HOMES ============ */}
      <section className="py-14 lg:py-16 bg-white border-t border-gray-200" data-id="element-1602">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1603">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-1604">
            <span className="text-xs font-extrabold tracking-[0.15em] uppercase text-yarbo-dark/70 mb-3 block" data-id="element-1605">
              Practical Robotic Yard Care
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-1606">
              Robotic Yard Care That Makes Sense for Real Homes
            </h2>
            <p className="text-lg text-gray-700" data-id="element-1607">
              Buying a yard robot is a big decision. Yarbo is designed to answer
              the practical questions homeowners have about coverage, battery
              life, setup, terrain, and year-round usefulness.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-id="element-1608">
            {[{
            icon: ShieldCheck,
            q: 'Will it cover my property?',
            a: 'Yarbo Lawn Mower supports large properties with scheduled mowing, multi-zone management, and coverage up to 6 acres twice per week.'
          }, {
            icon: CheckCircle2,
            q: 'What about battery life?',
            a: 'Yarbo is built around auto-recharge and resume operation, so it can return to the dock, charge, and continue instead of depending on one single run.'
          }, {
            icon: Leaf,
            q: 'Do I need boundary wires?',
            a: 'Yarbo uses app-defined virtual boundaries, so setup does not rely on burying physical perimeter wire.'
          }, {
            icon: Star,
            q: 'Can it handle slopes?',
            a: 'Supported Yarbo products are designed for slope handling up to 35 degrees / 70%, helping it work on more than flat suburban lawns.'
          }, {
            icon: Zap,
            q: 'Is it only for mowing?',
            a: 'No. With the right system and modules, Yarbo can support mowing, snow removal, leaf cleanup, trimming, and accessories.'
          }, {
            icon: ShieldCheck,
            q: 'Is this for new buyers or existing owners?',
            a: 'New buyers should start with complete systems or bundles. Existing Yarbo Core owners can shop compatible modules and accessories.'
          }].map((item, idx) => <motion.div key={item.q} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all rounded-2xl p-6" data-id="element-1609">
                <div className="w-10 h-10 rounded-lg bg-yarbo-dark flex items-center justify-center mb-4" data-id="element-1610">
                  <item.icon className="w-5 h-5 text-yarbo-yellow" data-id="element-1611" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-base" data-id="element-1612">
                  {item.q}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1613">
                  {item.a}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ NEW TO YARBO / EXISTING OWNER ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-1614">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1615">
          <motion.div {...fadeIn} className="text-center max-w-2xl mx-auto mb-8 lg:mb-10" data-id="element-1616">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-1617">
              New to Yarbo or Expanding a System?
            </h2>
            <p className="text-lg text-gray-700" data-id="element-1618">
              Choose the path that matches where you are. Most first-time buyers
              should start with a complete system.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-1619">
            <motion.div {...fadeIn} className="relative bg-gradient-to-br from-yarbo-yellow/15 to-amber-50 border-2 border-yarbo-yellow rounded-2xl p-8 lg:p-10 shadow-lg" data-id="element-1620">
              <div className="absolute top-5 right-5 text-[10px] font-bold uppercase tracking-wider text-yarbo-dark bg-yarbo-yellow px-2.5 py-1 rounded-full" data-id="element-1621">
                Most Common
              </div>
              <div className="w-12 h-12 rounded-xl bg-yarbo-dark flex items-center justify-center mb-5" data-id="element-1622">
                <ShieldCheck className="w-6 h-6 text-yarbo-yellow" data-id="element-1623" />
              </div>
              <h3 className="text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-1624">
                New to Yarbo
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1625">
                Start with a complete system or bundle that includes the Yarbo
                Core and the module you need.
              </p>
              <Link to="/category/complete-systems" className="inline-flex items-center bg-yarbo-dark text-white font-bold px-6 py-3.5 rounded-lg hover:bg-black transition-colors" data-id="element-1626">
                Shop Complete Systems <ChevronRight className="ml-2 w-4 h-4" data-id="element-1627" />
              </Link>
            </motion.div>

            <motion.div {...fadeIn} className="bg-white border-2 border-gray-200 hover:border-gray-300 rounded-2xl p-8 lg:p-10 transition-colors" data-id="element-1628">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-5" data-id="element-1629">
                <Zap className="w-6 h-6 text-yarbo-dark" data-id="element-1630" />
              </div>
              <h3 className="text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-1631">
                Already Own a Yarbo Core
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1632">
                Add compatible modules, replacement parts, batteries, blades,
                and accessories to expand your Yarbo system.
              </p>
              <Link to="/category/modules-accessories" className="inline-flex items-center bg-white text-yarbo-dark border-2 border-yarbo-dark font-bold px-6 py-3.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1633">
                Shop Modules &amp; Accessories{' '}
                <ChevronRight className="ml-2 w-4 h-4" data-id="element-1634" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ BUNDLES ============ */}
      <section className="py-14 lg:py-16 bg-white" data-id="element-1635">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1636">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-1637">
            <div className="max-w-2xl" data-id="element-1638">
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-1639">
                Bundle Yarbo for More Than One Outdoor Job
              </h2>
              <p className="text-lg text-gray-700" data-id="element-1640">
                Choose a bundle when you want one robotic platform for mowing,
                snow clearing, leaf cleanup, or all-season yard care.
              </p>
            </div>
            <Link to="/category/bundles" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-1641">
              View All Bundles <ChevronRight className="ml-2 w-5 h-5" data-id="element-1642" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-id="element-1643">
            {bundles.map(({
            product,
            bestFor
          }, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} data-id="element-1644">
                <ProductCard product={product} compact bestForLabel={bestFor} data-id="element-1645" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ ACCESSORIES ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-1646">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1647">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-1648">
            <div className="max-w-2xl" data-id="element-1649">
              <h2 className="text-2xl md:text-3xl font-extrabold text-yarbo-dark mb-2" data-id="element-1650">
                Yarbo Accessories &amp; Replacement Parts
              </h2>
              <p className="text-gray-700" data-id="element-1651">
                Shop blades, mounts, batteries, antennas, snow accessories,
                charging parts, covers, and maintenance items for Yarbo Y Series
                systems.
              </p>
            </div>
            <Link to="/category/accessories" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-1652">
              View All Accessories <ChevronRight className="ml-2 w-5 h-5" data-id="element-1653" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4" data-id="element-1654">
            {accessories.map((acc, idx) => <motion.div key={acc.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: Math.min(idx * 0.03, 0.3)
          }} className="group bg-white border border-gray-200 hover:border-gray-300 rounded-xl p-3 sm:p-4 hover:shadow-md transition-all flex flex-col" data-id="element-1655">
                <Link to={`/product/${acc.slug}`} className="block aspect-square bg-gray-50 rounded-lg p-3 mb-3 overflow-hidden" data-id="element-1656">
                  <img src={acc.image} alt={`${acc.name} - Yarbo accessory`} className="w-full h-full object-contain group-hover:scale-105 transition-transform" data-id="element-1657" />
                </Link>
                <Link to={`/product/${acc.slug}`} className="text-sm font-bold text-yarbo-dark hover:text-black line-clamp-2 mb-1 min-h-[2.5rem] leading-tight" data-id="element-1658">
                  {acc.name}
                </Link>
                <div className="text-base font-extrabold text-yarbo-dark mb-2" data-id="element-1659">
                  ${acc.price}
                </div>
                <button onClick={() => addToCart(acc)} className="mt-auto w-full bg-yarbo-dark hover:bg-black text-white font-bold text-xs py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5" data-id="element-1660">
                  <ShieldCheck className="w-3 h-3" data-id="element-1661" /> Add to Cart
                </button>
              </motion.div>)}
          </div>

          <div className="mt-8 text-center md:hidden" data-id="element-1662">
            <Link to="/category/accessories" className="inline-flex items-center font-bold text-yarbo-dark hover:text-black" data-id="element-1663">
              View All Accessories <ChevronRight className="ml-2 w-4 h-4" data-id="element-1664" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ COMPARE ============ */}
      <CompareSection data-id="element-1665" />

      {/* ============ WHY MEREM ROBOTICS ============ */}
      <section className="py-14 lg:py-16 bg-[#111111]" data-id="element-1666">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1667">
          <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-1668">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3" data-id="element-1669">
              Why Shop Yarbo From Merem Robotics?
            </h2>
            <p className="text-[#d1d5db] text-lg" data-id="element-1670">
              A focused online store for Yarbo robotic yard care — complete
              systems, modules, bundles, and accessories.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-id="element-1671">
            {[{
            icon: ShieldCheck,
            title: 'Yarbo Product Selection',
            desc: 'Shop complete systems, bundles, modules, and accessories in one place.'
          }, {
            icon: ShieldCheck,
            title: 'Nationwide Shipping',
            desc: 'Merem Robotics is Michigan-based and sells Yarbo products online for customers nationwide.'
          }, {
            icon: CheckCircle2,
            title: 'Secure Online Checkout',
            desc: 'Clear product pages, visible pricing, cart buttons, and a checkout-focused shopping flow.'
          }, {
            icon: ShieldCheck,
            title: 'Complete System Guidance',
            desc: 'New buyers can shop full systems and bundles instead of accidentally buying module-only products.'
          }, {
            icon: Zap,
            title: 'Module & Accessory Organization',
            desc: 'Existing Yarbo Core owners can find compatible modules, blades, batteries, and maintenance parts.'
          }, {
            icon: ShieldCheck,
            title: 'Real Yarbo Product Images',
            desc: 'Product photography and product names match actual Yarbo systems and modules.'
          }].map(({
            icon: Icon,
            title,
            desc
          }) => <div key={title} className="bg-[#1f1f1f] border border-white/5 p-6 rounded-2xl" data-id="element-1672">
                <div className="w-11 h-11 rounded-xl bg-yarbo-yellow/15 flex items-center justify-center mb-4" data-id="element-1673">
                  <Icon className="w-5 h-5 text-yarbo-yellow" data-id="element-1674" />
                </div>
                <h3 className="font-bold text-base mb-2 text-white" data-id="element-1675">{title}</h3>
                <p className="text-sm text-[#cbd5e1] leading-relaxed" data-id="element-1676">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-16 lg:py-20 bg-yarbo-yellow text-yarbo-dark text-center px-4" data-id="element-1677">
        <div className="max-w-3xl mx-auto" data-id="element-1678">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-5 leading-tight" data-id="element-1679">
            Ready to Take Yard Work Off Your Weekly List?
          </h2>
          <p className="text-lg mb-8 opacity-90" data-id="element-1680">
            Shop Yarbo robot lawn mowers, robotic snow blowers, leaf blowers,
            bundles, modules, and accessories online from Merem Robotics.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1681">
            <Link to="/shop" className="inline-flex items-center justify-center bg-yarbo-dark text-white font-bold px-8 py-4 rounded-lg hover:bg-black transition-colors" data-id="element-1682">
              <ShieldCheck className="w-5 h-5 mr-2" data-id="element-1683" /> Shop Yarbo Products
            </Link>
            <Link to="/compare" className="inline-flex items-center justify-center bg-white text-yarbo-dark font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1684">
              Compare Systems
            </Link>
            <Link to="/category/bundles" className="inline-flex items-center justify-center bg-transparent text-yarbo-dark border-2 border-yarbo-dark font-bold px-8 py-4 rounded-lg hover:bg-yarbo-dark/10 transition-colors" data-id="element-1685">
              Shop Bundles
            </Link>
          </div>
        </div>
      </section>
    </div>;
}
// ============ Compare Section ============
function CompareSection() {
  const {
    addToCart
  } = useCart();
  const {
    products
  } = useStore();
  const getProductBySlug = (slug: string) => products.find(p => p.slug === slug);
  const slugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-all-season-pro-bundle'];
  const items = slugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<ReturnType<typeof getProductBySlug>> => Boolean(p));
  const rows = [{
    label: 'Best for',
    values: ['Standard mowing', 'Heavy/steep mowing', 'Winter snow clearing', 'Leaves & debris', 'Year-round automation']
  }, {
    label: 'Main task',
    values: ['Lawn care', 'Lawn care (Pro)', 'Snow removal', 'Leaf cleanup', 'All-season']
  }, {
    label: 'Property type',
    values: ['Large lawns', 'Demanding lawns', 'Driveways & paths', 'Wooded lots', 'Mixed property']
  }, {
    label: 'Auto-recharge',
    values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes']
  }, {
    label: 'Includes Core',
    values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes']
  }, {
    label: 'Best buyer type',
    values: ['Standard lawns', 'Demanding lawns', 'Winter homes', 'Wooded lots', 'New all-in-one buyer']
  }];
  return <>
      <section className="py-14 lg:py-16 bg-white border-t border-gray-200" data-id="element-1686">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1687">
          <div className="text-center max-w-2xl mx-auto mb-10" data-id="element-1688">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-1689">
              Compare Yarbo Systems
            </h2>
            <p className="text-gray-700 text-lg" data-id="element-1690">
              Choose based on the outdoor job you want to automate first.
            </p>
          </div>

          {/* Desktop */}
          <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-1691">
            <table className="w-full" data-id="element-1692">
              <thead data-id="element-1693">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1694">
                  <th className="p-5 text-left text-xs font-bold uppercase tracking-wider text-gray-700 w-1/6" data-id="element-1695">
                    Feature
                  </th>
                  {items.map((p, i) => <th key={p.id} className={`p-5 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-1696">
                      <div className="aspect-square w-24 mx-auto mb-3 bg-gray-50 rounded-lg p-2" data-id="element-1697">
                        <img src={p.image} alt={`${p.name} comparison`} className="w-full h-full object-contain" data-id="element-1698" />
                      </div>
                      <div className="text-sm font-bold text-yarbo-dark mb-1" data-id="element-1699">
                        {p.name}
                      </div>
                      <div className="text-lg font-extrabold text-yarbo-dark mb-2" data-id="element-1700">
                        ${p.price.toLocaleString()}
                      </div>
                      <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 ring-1 ring-green-200 text-[10px] font-bold rounded-full px-2 py-0.5" data-id="element-1701">
                        <CheckCircle2 className="w-2.5 h-2.5" data-id="element-1702" /> In Stock
                      </span>
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1703">
                {rows.map((row, idx) => <tr key={idx} data-id="element-1704">
                    <td className="p-5 font-bold text-gray-800 text-sm" data-id="element-1705">
                      {row.label}
                    </td>
                    {row.values.map((val, i) => <td key={i} className={`p-5 text-center text-sm ${i === 4 ? 'bg-yarbo-yellow/5 font-medium text-yarbo-dark' : 'text-gray-700'}`} data-id="element-1706">
                        {val}
                      </td>)}
                  </tr>)}
                <tr className="bg-gray-50" data-id="element-1707">
                  <td className="p-5" data-id="element-1708"></td>
                  {items.map((p, i) => <td key={p.id} className={`p-4 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-1709">
                      <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-1710">
                        <ShieldCheck className="w-4 h-4" data-id="element-1711" /> Add to Cart
                      </button>
                      <Link to={`/product/${p.slug}`} className="block mt-2 text-xs font-bold text-gray-700 hover:text-yarbo-dark" data-id="element-1712">
                        View Details →
                      </Link>
                    </td>)}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile stacked */}
          <div className="lg:hidden space-y-4" data-id="element-1713">
            {items.map((p, i) => <div key={p.id} className={`bg-white rounded-xl border p-5 ${i === 4 ? 'border-yarbo-yellow bg-yarbo-yellow/5' : 'border-gray-200'}`} data-id="element-1714">
                <div className="flex gap-4 mb-4" data-id="element-1715">
                  <div className="w-20 h-20 bg-gray-50 rounded-lg p-2 flex-shrink-0" data-id="element-1716">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-1717" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1718">
                    <h3 className="font-bold text-yarbo-dark mb-1 leading-tight" data-id="element-1719">
                      {p.name}
                    </h3>
                    <div className="text-xl font-extrabold text-yarbo-dark mb-2" data-id="element-1720">
                      ${p.price.toLocaleString()}
                    </div>
                    <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 ring-1 ring-green-200 text-[10px] font-bold rounded-full px-2 py-0.5" data-id="element-1721">
                      <CheckCircle2 className="w-2.5 h-2.5" data-id="element-1722" /> In Stock
                    </span>
                  </div>
                </div>
                <dl className="space-y-1.5 text-sm mb-4" data-id="element-1723">
                  {rows.map((row, idx) => <div key={idx} className="flex justify-between gap-3" data-id="element-1724">
                      <dt className="text-gray-500" data-id="element-1725">{row.label}</dt>
                      <dd className="text-gray-800 font-medium text-right" data-id="element-1726">
                        {row.values[i]}
                      </dd>
                    </div>)}
                </dl>
                <div className="flex gap-2" data-id="element-1727">
                  <button onClick={() => addToCart(p)} className="flex-1 bg-yarbo-dark text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2" data-id="element-1728">
                    <ShieldCheck className="w-4 h-4" data-id="element-1729" /> Add to Cart
                  </button>
                  <Link to={`/product/${p.slug}`} className="flex-1 text-center text-yarbo-dark border border-gray-200 font-bold py-3 rounded-lg text-sm" data-id="element-1730">
                    View Details
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </>;
}